import React from "react";
import { TrendingUp, Users, Target } from "lucide-react";
import DataPanel from "../DataPanel";
import EmptyState from "../EmptyState";
import WidgetExportBtn from "../../reports/WidgetExportBtn";
import { flattenCompetitors } from "../../reports/exportDataUtils";

interface CompetitorsTabContentProps {
  liveData?: any | null;
}

const CompetitorsTabContent: React.FC<CompetitorsTabContentProps> = ({ liveData }) => {
  const competitors = liveData?.competitors ?? [];
  const hasCompetitors = Array.isArray(competitors) && competitors.length > 0;
  const keywordGaps = liveData?.competitors?.flatMap((c: any) => c.keywordGaps || [])

  const opportunityColor = (opp: string) => {
    const normalized = (opp || "").toUpperCase();
    if (normalized === "HIGH") return { bg: "rgba(74,222,128,0.15)", text: "#4ADE80", border: "rgba(74,222,128,0.3)" };
    if (normalized === "LOW") return { bg: "rgba(148,163,184,0.15)", text: "#94A3B8", border: "rgba(148,163,184,0.3)" };
    return { bg: "rgba(255,154,74,0.15)", text: "#FF9A4A", border: "rgba(255,154,74,0.3)" };
  };

  return (
    <div className="space-y-6">
      {/* Competitor Comparison */}
      <DataPanel
        title="Competitor Comparison"
        icon={<Users className="w-4 h-4" />}
        actions={<WidgetExportBtn title="Competitor Comparison" data={flattenCompetitors(competitors)} />}
      >
        {hasCompetitors ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: "1px solid rgba(139,123,247,0.1)" }}>
                  {["Domain", "Authority", "Est. Traffic", "Common Keywords", "Overlap %"].map((h) => (
                    <th key={h} className="text-left py-2 px-2 text-xs font-medium" style={{ color: "#9AA0B3" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {competitors.map((comp: any, i: number) => (
                  <tr key={i} style={{ borderBottom: "1px solid rgba(139,123,247,0.06)" }}>
                    <td className="py-2.5 px-2" style={{ color: "#C4BDE0" }}>{comp.domain}</td>
                    <td className="py-2.5 px-2">
                      <span className="font-bold" style={{ color: "#E8E4FF" }}>{comp.authorityScore ?? comp.authority ?? "—"}</span>
                      <span className="text-xs ml-1" style={{ color: "#9AA0B3" }}>/100</span>
                    </td>
                    <td className="py-2.5 px-2" style={{ color: "#FF9A4A" }}>{(comp.traffic ?? 0).toLocaleString()}</td>
                    <td className="py-2.5 px-2" style={{ color: "#8B7BF7" }}>{(comp.commonKeywords ?? "—").toLocaleString()}</td>
                    <td className="py-2.5 px-2">
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-1.5 rounded-full" style={{ background: "rgba(139,123,247,0.15)" }}>
                          <div className="h-full rounded-full" style={{ width: `${Math.min(comp.overlap ?? comp.commonKeywords ?? 0, 100)}%`, background: "#8B7BF7" }} />
                        </div>
                        <span className="text-xs" style={{ color: "#9AA0B3" }}>{comp.overlap ?? comp.commonKeywords ?? 0}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            title="No competitor data yet"
            description="Run a Competitor Analysis audit to see your competitive landscape."
          />
        )}
      </DataPanel>

      {/* Keyword Gap Analysis */}
      <DataPanel
        title="Keyword Gap Analysis"
        icon={<Target className="w-4 h-4" />}
        actions={<WidgetExportBtn title="Keyword Gap Analysis" data={keywordGaps?.map((g: any) => ({ Keyword: g.keyword, "Their Pos.": g.competitorPosition, "Your Pos.": g.yourPosition || "Not ranking", Volume: g.searchVolume, Opportunity: g.opportunity || g.gapType || "MED" })) || []} />}
      >
        {hasCompetitors && keywordGaps?.length > 0 ? (
          <>
            <div className="flex flex-wrap gap-2 mb-4">
              {["Missing", "Weak", "Strong"].map((label, i) => (
                <button key={label} className="px-3 py-1.5 rounded-full text-xs font-medium transition-all"
                  style={{
                    background: i === 0 ? "rgba(91,61,245,0.2)" : "rgba(255,255,255,0.05)",
                    border: `1px solid ${i === 0 ? "rgba(91,61,245,0.3)" : "rgba(139,123,247,0.15)"}`,
                    color: i === 0 ? "#8B7BF7" : "#9AA0B3",
                  }}>
                  {label}
                </button>
              ))}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: "1px solid rgba(139,123,247,0.1)" }}>
                    {["Keyword", "Their Pos.", "Your Pos.", "Volume", "Opportunity"].map((h) => (
                      <th key={h} className="text-left py-2 px-2 text-xs font-medium" style={{ color: "#9AA0B3" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {keywordGaps.slice(0, 10).map((gap: any, i: number) => {
                    const rawOpp = gap.opportunity || gap.gapType || "MED";
                    const opp = rawOpp === "missing" ? "HIGH" : rawOpp === "losing" ? "MED" : rawOpp === "winning" ? "LOW" : rawOpp;
                    const colors = opportunityColor(opp);
                    return (
                      <tr key={i} style={{ borderBottom: "1px solid rgba(139,123,247,0.06)" }}>
                        <td className="py-2.5 px-2" style={{ color: "#C4BDE0" }}>{gap.keyword}</td>
                        <td className="py-2.5 px-2" style={{ color: "#4ADE80" }}>#{gap.competitorPosition ?? gap.competitorPos ?? "—"}</td>
                        <td className="py-2.5 px-2" style={{ color: (gap.yourPosition || gap.yourPos) ? "#F87171" : "#9AA0B3" }}>
                          {(gap.yourPosition || gap.yourPos) ? `#${gap.yourPosition || gap.yourPos}` : "Not ranking"}
                        </td>
                        <td className="py-2.5 px-2" style={{ color: "#9AA0B3" }}>{(gap.searchVolume ?? gap.volume ?? 0).toLocaleString()}</td>
                        <td className="py-2.5 px-2">
                          <span className="text-xs px-2 py-1 rounded-full font-medium"
                            style={{ background: colors.bg, color: colors.text, border: `1px solid ${colors.border}` }}>
                            {opp}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <EmptyState
            title="No keyword gap data yet"
            description="Run a Competitor Analysis audit to find keyword gaps between you and competitors."
          />
        )}
      </DataPanel>

      {/* Market Share Indicators */}
      <DataPanel
        title="Estimated Market Share"
        icon={<TrendingUp className="w-4 h-4" />}
        actions={<WidgetExportBtn title="Market Share" data={competitors?.map((c: any) => ({
          Domain: c.domain, "Est. Traffic": c.traffic, "Market Share%": `${c.share}%`
        })) || []} />}
      >
        {hasCompetitors ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: "1px solid rgba(139,123,247,0.1)" }}>
                  {["Domain", "Est. Traffic", "Market Share%"].map((h) => (
                    <th key={h} className="text-left py-2 px-2 text-xs font-medium" style={{ color: "#9AA0B3" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {competitors.map((comp: any, i: number) => (
                  <tr key={i} style={{ borderBottom: "1px solid rgba(139,123,247,0.06)" }}>
                    <td className="py-2.5 px-2" style={{ color: "#C4BDE0" }}>{comp.domain}</td>
                    <td className="py-2.5 px-2" style={{ color: "#FF9A4A" }}>{(comp.traffic ?? 0).toLocaleString()}</td>
                    <td className="py-2.5 px-2">
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-1.5 rounded-full" style={{ background: "rgba(139,123,247,0.15)" }}>
                          <div className="h-full rounded-full" style={{ width: `${Math.min(comp.share ?? 0, 100)}%`, background: "#8B7BF7" }} />
                        </div>
                        <span className="text-xs" style={{ color: "#9AA0B3" }}>{comp.share ?? 0}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            title="No market share data yet"
            description="Run a Competitor Analysis audit to estimate market share distribution."
          />
        )}
      </DataPanel>
    </div>
  );
};

export default CompetitorsTabContent;
